import { byAuthorityKeyFindRoute, findMatchingRoute } from '@/hooks/useCurrentRoute';
import dayjs from 'dayjs';
import { matchPath } from 'react-router-dom';
import { Routes, TrackingEventData, TrackingEventType } from './types';
/**
 * 获取当前路由的标题
 * @param path 路径
 * @param routeList 路由列表
 * @returns 页面标题
 */
export function getPageTitle(path: string, routeList: Routes[] = []): string {
  // 1. 精确匹配优先
  const exactMatch = routeList.find((route) => route.path === path);
  if (exactMatch) {
    return exactMatch.name || exactMatch.meta?.title || '';
  }

  // 2. 参数匹配
  for (const route of routeList) {
    if (route?.path?.includes(':')) {
      const match = matchPath(route.path, path);
      // console.log('match', match);
      if (match) {
        return route.name || route.meta?.title || '';
      }
    }
  }

  // 3. 递归查找子路由
  for (const route of routeList) {
    if (route.children) {
      const childMatch = getPageTitle(path, route.children);
      if (childMatch) {
        return childMatch;
      }
    }
  }

  return '';
}

/**
 * 路由数据过滤
 * @param path 路径
 * @returns 匹配的路由
 */
export function filterRoute(path: string) {
  if (path?.indexOf('?') > -1) {
    const [pathname, search] = path.split('?');
    const searchParams = new URLSearchParams(search);
    const key = searchParams.get('key');

    if (key) {
      const newPath = `${pathname}/${key}`;
      return byAuthorityKeyFindRoute(newPath);
    }
  }

  return findMatchingRoute(path);
}

/**
 * 处理页面浏览事件
 * @param currentPagePath 当前页面路径
 * @param pageStartTime 页面开始时间
 * @param previousPagePath 上一个页面路径
 * @param pageTitle 当前页面标题
 * @param dictKey 字典key
 * @param productName 产品名称
 * @param data 额外数据
 * @param track 追踪函数
 * @returns 更新后的页面信息
 */
export function handlePageView(
  currentPagePath: string,
  pageStartTime: number,
  previousPagePath: string,
  pageTitle: string | undefined,
  dictKey: string | undefined,
  productName: string | undefined,
  data?: Partial<
    Pick<TrackingEventData, 'pagePath' | 'dictKey' | 'productName' | 'pageTitle'> & {
      pagePath?: string;
      reload?: boolean;
    }
  >,
  track?: (data: any) => void,
): {
  currentPagePath: string;
  pageStartTime: number;
  previousPagePath: string;
  pageTitle: string | undefined;
  dictKey: string | undefined;
  productName: string | undefined;
} {
  const { pathname } = location;
  const { search } = location;
  const path = data?.pagePath || `${pathname}${search}`;

  // 如果是同一个页面，不重复记录
  if (currentPagePath === path && !data?.reload) {
    return {
      currentPagePath,
      pageStartTime,
      previousPagePath,
      pageTitle,
      dictKey,
      productName,
    };
  }
  const currentTime = dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss')).toDate().getTime();
  // 如果有上一个页面，记录上一个页面的停留时间
  if (currentPagePath && pageStartTime > 0) {
    const stayDuration = (currentTime - pageStartTime) / 1000;
    if (track) {
      track({
        eventType: TrackingEventType.PageStay,
        eventName: '页面停留',
        stayDuration,
        pagePath: currentPagePath,
        pageTitle: pageTitle || data?.pageTitle,
        previousPagePath, // 添加上一个页面路径
        dictKey,
        productName,
      });
    }
  }

  if (data?.reload) {
    return {
      currentPagePath,
      pageStartTime,
      previousPagePath,
      pageTitle,
      dictKey,
      productName,
    };
  }

  // 更新当前页面信息
  const newPageTitle = data?.pageTitle || undefined;
  const newDictKey = data?.dictKey;
  const newProductName = data?.productName;
  const newPreviousPagePath = path;

  // 记录页面浏览事件
  if (track) {
    track({
      eventType: TrackingEventType.PageView,
      eventName: '页面浏览',
      pagePath: path,
      stayDuration: 0,
      pageTitle: data?.pageTitle,
      dictKey: data?.dictKey,
      productName: data?.productName,
    });
  }

  return {
    currentPagePath: path,
    pageStartTime: dayjs(dayjs().format('YYYY-MM-DD HH:mm:ss')).toDate().getTime(),
    previousPagePath: newPreviousPagePath,
    pageTitle: newPageTitle,
    dictKey: newDictKey,
    productName: newProductName,
  };
}
